/**
 * Where a playEngine build is waiting, as one line — for every export path that
 * builds in the agentContainer (versionedPlayEngine export, demo link, batch,
 * test playable; snapshot editor export, share link, task preview).
 *
 * Before the first artifact lands a build can spend minutes fetching the
 * engine, installing its dependencies once after a lockfile change, or queued
 * behind other people's exports: the container runs one webpack at a time for
 * every dashboard. The server forwards those as job progress
 * (controller/playEngine/containerProgress.js); this turns the newest of them
 * into the line a person reads, so a long wait does not look like a hang.
 *
 * `key` changes when the step does and stays put while a queued build only
 * moves up the line, which is what an elapsed-time display resets on.
 */

const ENGINE_STEP_LABELS = {
  fetch: "Fetching the latest engine",
  install: "Installing engine dependencies (one-off, can take a few minutes)",
};

/** A snapshot editor cell names itself; a version is named by the caller. */
function defaultNameOf(event) {
  if (event.sipLabel) return event.sipLabel;
  if (event.snapshot) return String(event.snapshot).replace(/\.json$/i, "");
  return null;
}

function targetOf(event, nameOf) {
  return [nameOf(event), event.network, event.language].filter(Boolean).join(" · ");
}

/**
 * @param {object[]} events  the job's progress so far
 * @param {object} [options]
 * @param {Function} [options.nameOf]       event → display name of the cell, or null
 * @param {object}   [options.stageLabels]  stage → label, for the stages this
 *   caller has no line of its own for. A stage WITHOUT a label ends the search:
 *   the caller is past the build and describes that part itself.
 * @returns {{ label: string, key: string } | null}
 */
export function describeBuildWait(events, { nameOf = defaultNameOf, stageLabels = {} } = {}) {
  const list = events || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const event = list[i];
    if (!event) continue;

    if (event.type === "stage") {
      const label = stageLabels[event.stage];
      return label ? { label, key: `${i}:stage:${event.stage}` } : null;
    }

    if (event.type === "engineStep" && ENGINE_STEP_LABELS[event.step]) {
      return { label: ENGINE_STEP_LABELS[event.step], key: `${i}:engineStep:${event.step}` };
    }

    if (event.type === "queued") {
      const ahead = Number(event.ahead) || 0;
      const target = targetOf(event, nameOf);
      return {
        label:
          `Waiting for the build server — ${ahead} build${ahead === 1 ? "" : "s"} ahead of you` +
          (target ? ` (${target})` : ""),
        // One wait per cell, however often its place in line changes.
        key: `queued:${event.index}:${event.versionID || event.snapshot || ""}:${event.network || ""}:${event.language || ""}`,
      };
    }

    if (event.type === "building") {
      const count = event.index && event.total ? ` ${event.index}/${event.total}` : "";
      const target = targetOf(event, nameOf);
      return { label: `Building${count}${target ? ` — ${target}` : ""}`, key: `${i}:building` };
    }
    // built, build_failed, warnings…: the step before them is still the story
  }
  return null;
}
